import { mdiDatabaseOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Grid2 as Grid, Stack, SxProps, Typography } from '@mui/material';
import React, { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { IEplassProjectData } from '../../../../redux/user/sliceUserProjects';
import { vars } from '../../../../Theme';
import { IProjectContextMenuConfig } from './ProjectContextMenu';
import ProjectFavouriteButton from './ProjectFavouriteButton';
import ProjectRowTag from './ProjectRowTag';
import i18n from '../../../../localization/i18n';

/** Style rules for the project tile. */
const cardSx: SxProps = {
    border: `1px solid ${vars.lightGray}`,
    borderRadius: 1,
    height: 112,
    px: 1,

    '&:hover': {
        bgcolor: vars.fillGray,
        cursor: 'pointer',
    },
};

/**
 * Component that displays a user project as a tile.
 */
export default (props: IProjectTileProps) => {
    /** Deconstruct props. */
    const { project, favourite, contextMenu } = props;

    /** Allows the component to navigate to other views in the application.  */
    const navigate = useNavigate();

    return (
        <Grid
            size={{ xs: 12 / props.size }}
            sx={{ ...props.style }}
            onClick={() => navigate(`/project/${project.replicaId}`)}
            onContextMenu={e => contextMenu.callback(e, project)}
        >
            <Stack sx={cardSx}>
                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'center',
                        pt: 1,
                        pl: 1,
                    }}
                >
                    <ProjectRowTag
                        moduleAbbreviation={project.module.moduleAbbreviation}
                        tagColor={project.module.tagColor}
                        tooltipTitle={project.module.translations[i18n.language]}
                        key={project.replicaId}
                    />

                    <Box sx={{ color: vars.concrete, fontSize: 14, flex: 1 }}>
                        {project.category.slice(0, -1)}
                    </Box>

                    <ProjectFavouriteButton replicaId={project.replicaId} favourite={favourite} />
                </Stack>

                <Stack direction='row' sx={{ p: 1 }}>
                    <Icon path={mdiDatabaseOutline} size={1} style={{ marginTop: -2 }} />

                    <Typography variant='h6' sx={{ fontSize: 16, ml: 1, lineHeight: 1.2 }}>
                        {project.title}
                    </Typography>
                </Stack>
            </Stack>
        </Grid>
    );
};

/**
 * Interface that describes the properties needed for ProjectTile.
 */
export interface IProjectTileProps {
    /** Eplass Project Object. */
    project: IEplassProjectData;

    /** Project favourite status. */
    favourite: boolean;

    /** Tile grid size. */
    size: number;

    /** (Optional) Additional CSS styles. */
    style?: CSSProperties;

    contextMenu: IProjectContextMenuConfig;
}
